import { useState, useEffect } from 'react';

// เก็บไว้เครื่องนี้เครื่องเดียว (localStorage) ไม่ส่งไปไหน — ใช้แค่เติมฟอร์มให้อัตโนมัติรอบหน้า
const REMEMBER_KEY = 'adminLoginRemember';

function loadRemembered() {
  try {
    const raw = localStorage.getItem(REMEMBER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveRemembered(username, password) {
  try { localStorage.setItem(REMEMBER_KEY, JSON.stringify({ username, password })); } catch { /* private mode — ไม่จำก็ไม่เป็นไร */ }
}

function clearRemembered() {
  try { localStorage.removeItem(REMEMBER_KEY); } catch { /* ไม่เป็นไร */ }
}

export default function Login({ onLogin, apiUrl }) {
  const remembered = loadRemembered();
  const [username, setUsername] = useState(remembered?.username || '');
  const [password, setPassword] = useState(remembered?.password || '');
  const [rememberMe, setRememberMe] = useState(!!remembered);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true
  );

  // เลย์เอาต์ซ้าย/ขวา (≥1024px) vs ฟอร์มอย่างเดียว (<1024px) — ใช้ window.matchMedia
  // แบบเดียวกับ Checkout.jsx (pattern เดิมของโปรเจกต์ ไม่คิดวิธีใหม่)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${apiUrl}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      const result = await response.json();

      if (response.ok && result.data?.token) {
        if (rememberMe) saveRemembered(username, password);
        else clearRemembered();
        // เจ้าของร้าน (ผ่าน .env) หรือผู้จัดการสาขา (ผ่านตาราง employees, role: manager)
        // ล็อกอินหน้าเดียวกันนี้ได้ทั้งคู่ — backend คืน user.role/user.branchId มาบอกว่าเป็นใคร
        onLogin(result.data.token, result.data.user);
      } else if (response.status === 429) {
        // strictRateLimit บน /admin/login — ข้อความเดิม "ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง"
        // ทำให้คนพิมพ์รหัสถูกแต่โดน rate limit เข้าใจผิดว่ารหัสตัวเองผิด (อนุมัติแยกจากงาน UI)
        setError('ลองเข้าระบบบ่อยเกินไป กรุณารอสักครู่แล้วลองใหม่');
      } else {
        setError('ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ กรุณาลองใหม่');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100dvh', background: 'var(--bg)' }}>
      {isDesktop && (
        <div style={{
          width: '560px',
          flexShrink: 0,
          background: 'var(--sidebar)',
          borderRight: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '3rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <img
              src="/logo.jpg"
              alt=""
              style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
            />
            <div>
              <div style={{ color: 'var(--accent)', fontWeight: 700, fontSize: '1.1rem' }}>ชาบู ฮิดะ ปิ้งย่าง</div>
              <div style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>ระบบจัดการร้าน</div>
            </div>
          </div>

          <div>
            <div style={{ width: '48px', height: '4px', borderRadius: '999px', background: 'var(--accent)', marginBottom: '1.5rem' }} />
            <h1 style={{ color: 'var(--text)', fontSize: '2.2rem', fontWeight: 700, marginBottom: '1rem', lineHeight: 1.3 }}>
              ระบบหลังร้าน สำหรับพนักงาน
            </h1>
            <p style={{ color: 'var(--text-2)', fontSize: '1.05rem', lineHeight: 1.6 }}>
              จัดการโต๊ะ ออเดอร์ ครัว และการชำระเงิน ได้ในที่เดียว
            </p>
          </div>

          <div style={{ color: 'var(--faint)', fontSize: '0.85rem' }}>admin.chabuhida.com</div>
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ width: '100%', maxWidth: '400px' }}>
          {!isDesktop && (
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <img
                src="/logo.jpg"
                alt=""
                style={{ width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover', margin: '0 auto 0.75rem', display: 'block' }}
              />
              <div style={{ color: 'var(--accent)', fontWeight: 700, fontSize: '1.05rem' }}>ชาบู ฮิดะ ปิ้งย่าง</div>
              <div style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>ระบบจัดการร้าน</div>
            </div>
          )}

          <h2 style={{ color: 'var(--text)', fontSize: '1.6rem', fontWeight: 700, marginBottom: '0.4rem' }}>เข้าสู่ระบบ</h2>
          <p style={{ color: 'var(--muted)', fontSize: '0.95rem', marginBottom: '2rem' }}>ใช้บัญชีพนักงานของคุณ</p>

          {error && (
            <div style={{
              background: '#2a1517',
              border: '1px solid #8a2f33',
              color: '#ff9b9b',
              padding: '0.9rem',
              borderRadius: '10px',
              marginBottom: '1.5rem',
              fontSize: '0.9rem',
              textAlign: 'center',
              fontWeight: 500
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text)', fontWeight: 600, fontSize: '0.9rem' }}>
                ชื่อผู้ใช้
              </label>
              <input
                type="text"
                className="dark-input"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                required
                style={{
                  width: '100%',
                  minHeight: '48px',
                  padding: '0.75rem 1rem',
                  border: '1px solid var(--border-strong)',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text)', fontWeight: 600, fontSize: '0.9rem' }}>
                รหัสผ่าน
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="dark-input"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  style={{
                    width: '100%',
                    minHeight: '48px',
                    padding: '0.75rem 3rem 0.75rem 1rem',
                    border: '1px solid var(--border-strong)',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
                  style={{
                    position: 'absolute',
                    right: '0.5rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--muted)',
                    cursor: 'pointer',
                    padding: '0.5rem',
                    minHeight: '44px',
                    minWidth: '44px'
                  }}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.6rem',
              marginBottom: '1.5rem',
              color: 'var(--text-2)',
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              จำชื่อผู้ใช้และรหัสผ่านบนเครื่องนี้
            </label>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                minHeight: '52px',
                padding: '0.9rem',
                background: loading ? 'var(--surface-2)' : 'var(--accent)',
                color: loading ? 'var(--muted)' : '#1a1206',
                border: 'none',
                borderRadius: '10px',
                fontSize: '1.05rem',
                fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
            </button>
          </form>

          <p style={{ textAlign: 'center', color: 'var(--faint)', fontSize: '0.85rem', marginTop: '1.5rem' }}>
            ลืมรหัสผ่าน ติดต่อเจ้าของร้าน
          </p>
        </div>
      </div>
    </div>
  );
}
